import React, { FC, useEffect } from 'react';
import { useTranslation } from '@blocs.i18n';
import { BasicContainer, BodyText, FlexCol, FlexRow } from '@minecraft.atoms';
import { usePermissions } from '@minecraft.permissions';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { helpers, useDeviceAndBrowserInfo } from '@minecraft.utils';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import {
  InstagramSocial,
  InstagramButton,
  SocialPaywallSection,
  YoutubeSocial,
  YoutubeButton,
  TikTokButton,
} from '@blocs.social';
import { AppType } from '@blocs.common';
import { SocialMediaPlatform } from '@minecraft.graphql-operations';
import { useProfileViewContext } from '../ProfileViewContext';

const SocialButtonContainer: FC = ({ children }) => (
  <FlexRow
    alignItems="center"
    justifyContent="center"
    bgColor="cardLightContrast"
    borderRadius="md"
    className="cn_atom_p-2"
    w="full"
    mdProps={{ w: 'max', className: 'cn_atom_p-2' }}
  >
    {children}
  </FlexRow>
);

const Preview = ({
  isInstagramConnected,
  instagramFollowerCount,
  instagramProfileLink,
  isYouTubeConnected,
  youtubeFollowerCount,
  youtubeProfileLink,
  isTikTokConnected,
  tiktokFollowerCount,
  tiktokProfileLink,
  isTikTokSocialLinkingEnabled,
}: {
  isInstagramConnected: boolean;
  instagramFollowerCount: number;
  instagramProfileLink: string;
  isYouTubeConnected: boolean;
  youtubeFollowerCount: number;
  youtubeProfileLink: string;
  isTikTokConnected: boolean;
  tiktokFollowerCount: number;
  tiktokProfileLink: string;
  isTikTokSocialLinkingEnabled: boolean;
}) => {
  const { t } = useTranslation();
  const showTikTok = isTikTokSocialLinkingEnabled && isTikTokConnected;
  if (!isInstagramConnected && !isYouTubeConnected && !showTikTok) return null;

  return (
    <BasicContainer
      data-testid="my-socials-container-preview"
      borderPlacement="bottom"
      className="cn_atom_py-4 cn_mt-2"
    >
      <FlexCol gap="2">
        <FlexRow alignItems="center" gap="2">
          <BodyText as="h2" fontWeight="semibold">
            {t('common:profile.info.preview.socialLinks')}
          </BodyText>
        </FlexRow>
        <FlexRow gap="4" w="full" mdProps={{ w: 'fit' }}>
          {isInstagramConnected && (
            <SocialButtonContainer>
              <InstagramButton
                label={t('common:profile.info.social.instagram.followerCount', {
                  formattedCount: helpers.formatMetricCount(instagramFollowerCount, true),
                })}
                readOnly
                profileLink={instagramProfileLink}
                isInstagramConnected={isInstagramConnected}
              />
            </SocialButtonContainer>
          )}
          {isYouTubeConnected && (
            <SocialButtonContainer>
              <YoutubeButton
                label={t('common:profile.info.social.youtube.subscriberCount', {
                  formattedCount: helpers.formatMetricCount(youtubeFollowerCount, true),
                })}
                readOnly
                profileLink={youtubeProfileLink}
                isYoutubeConnected={isYouTubeConnected}
              />
            </SocialButtonContainer>
          )}
          {showTikTok && (
            <SocialButtonContainer>
              <TikTokButton
                label={t('common:profile.info.social.instagram.followerCount', {
                  formattedCount: helpers.formatMetricCount(tiktokFollowerCount, true),
                })}
                readOnly
                profileLink={tiktokProfileLink}
                isTikTokConnected={isTikTokConnected}
              />
            </SocialButtonContainer>
          )}
        </FlexRow>
      </FlexCol>
    </BasicContainer>
  );
};

interface MySocialsProps {
  isPreview: boolean;
}

const MySocials = ({ isPreview }: MySocialsProps): JSX.Element => {
  const { t } = useTranslation();

  const isInstagramSocialLinkingEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_INSTAGRAM_SOCIAL_LINKING);
  const isYouTubeSocialLinkingEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_YOUTUBE_SOCIAL_LINKING);
  const isTikTokSocialLinkingEnabled = useFeatureExperiment(FEATURE_FLAGS.WEB_TIKTOK_SOCIAL_LINKING);

  const { ability } = usePermissions();
  const hasWriteAccess = ability.can('write', 'social-media');
  const { deviceInfo, browserInfo } = useDeviceAndBrowserInfo();
  const {
    appType,
    fetcher: {
      profile: { socialMedia },
    },
    updater: { handleInstagramAuth, deleteAllSocialData, disconnectSocialAccount, mutationLoading, handleYoutubeAuth },
  } = useProfileViewContext();
  const isTalent = appType === AppType.Talent;
  const isCasting = appType === AppType.Casting;

  const isInstagramConnected = socialMedia?.instagram?.isTokenValid;
  const instagramFollowerCount = socialMedia?.instagram?.metrics?.followerCount;
  const instagramProfileLink = socialMedia?.instagram?.link;

  const isYouTubeConnected = socialMedia?.youtube?.isTokenValid;
  const youtubeFollowerCount = socialMedia?.youtube?.metrics?.followerCount;
  const youtubeProfileLink = socialMedia?.youtube?.link;

  const isTikTokConnected = socialMedia?.tiktok?.isTokenValid;
  const tiktokFollowerCount = socialMedia?.tiktok?.metrics?.followerCount;
  const tiktokProfileLink = socialMedia?.tiktok?.link;

  useEffect(() => {
    if (isInstagramConnected && isTalent) {
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.TALENT_SOCIAL_FOLLOWER_COUNT_VIEW,
        data: {
          socialPlatform: SocialMediaPlatform.Instagram,
          followerCount: instagramFollowerCount,
          timestamp: new Date().toISOString(),
          device: deviceInfo,
          browser: browserInfo,
        },
      });
    }
    if (isYouTubeConnected && isTalent) {
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.TALENT_SOCIAL_FOLLOWER_COUNT_VIEW,
        data: {
          socialPlatform: SocialMediaPlatform.Youtube,
          followerCount: youtubeFollowerCount,
          timestamp: new Date().toISOString(),
          device: deviceInfo,
          browser: browserInfo,
        },
      });
    }

    if (isCasting) {
      captureHeapEvent({
        name: HEAP_EVENT_NAMES.CD_CLICK_TALENT_PROFILE,
        data: {
          hasInstagram: !!isInstagramConnected,
          hasYouTube: !!isYouTubeConnected,
          timestamp: new Date().toISOString(),
          device: deviceInfo,
          browser: browserInfo,
        },
      });
    }
  }, [
    instagramFollowerCount,
    deviceInfo,
    browserInfo,
    isInstagramConnected,
    isTalent,
    isYouTubeConnected,
    youtubeFollowerCount,
    appType,
    isCasting,
  ]);

  if (!isPreview && isTalent) {
    // displays the social media section on the profile edit view for talent users
    // if hasWriteAccess = true, they are able to connect/disconnect instagram
    // if hasWriteAccess = false, they have a CTA to upgrade to premium
    return (
      <BasicContainer data-testid="my-socials-container" borderPlacement="bottom" className="cn_atom_pb-4 cn_mt-2">
        <FlexRow alignItems="center" gap="2">
          <BodyText as="h2" fontWeight="semibold">
            {t('common:profile.info.mySocial')}
          </BodyText>
        </FlexRow>
        <BasicContainer mt="2">
          <BodyText>
            {isYouTubeSocialLinkingEnabled
              ? t('common:profile.info.mySocial.multi.description')
              : t('common:profile.info.mySocial.description')}
          </BodyText>
        </BasicContainer>
        <BasicContainer mt="2">
          {hasWriteAccess ? (
            <FlexRow gap="2" w="full" mdProps={{ w: 'fit' }}>
              {isInstagramSocialLinkingEnabled && (
                <InstagramSocial
                  deleteAllSocialData={() =>
                    deleteAllSocialData({
                      socialMediaPlatform: SocialMediaPlatform.Instagram,
                    })
                  }
                  disconnectSocialAccount={() =>
                    disconnectSocialAccount({
                      socialMediaPlatform: SocialMediaPlatform.Instagram,
                    })
                  }
                  eventSourcePage="social-upgrade"
                  handleInstagramAuth={() => handleInstagramAuth()}
                  mutationLoading={mutationLoading}
                  instagramDetails={socialMedia?.instagram}
                />
              )}
              {isYouTubeSocialLinkingEnabled && (
                <YoutubeSocial
                  deleteAllSocialData={() =>
                    deleteAllSocialData({
                      socialMediaPlatform: SocialMediaPlatform.Youtube,
                    })
                  }
                  disconnectSocialAccount={() =>
                    disconnectSocialAccount({
                      socialMediaPlatform: SocialMediaPlatform.Youtube,
                    })
                  }
                  eventSourcePage="social-upgrade"
                  handleYoutubeAuth={handleYoutubeAuth}
                  mutationLoading={mutationLoading}
                  youtubeDetails={socialMedia?.youtube}
                />
              )}
            </FlexRow>
          ) : (
            <SocialPaywallSection eventSourcePage="social-upgrade" />
          )}
        </BasicContainer>
      </BasicContainer>
    );
  }

  // any case besides the talent edit profile page sees the "Preview" component
  // if the profile is connected, you get a link to the instagram page
  // if the profile is not connected, nothing is rendered
  // this renders for all profile "Preview" and all public profile views
  // this also renders for the talent edit profile page in the reps app
  return (
    <Preview
      isInstagramConnected={isInstagramConnected}
      instagramFollowerCount={instagramFollowerCount}
      instagramProfileLink={instagramProfileLink}
      isYouTubeConnected={isYouTubeConnected}
      youtubeFollowerCount={youtubeFollowerCount}
      youtubeProfileLink={youtubeProfileLink}
      isTikTokConnected={isTikTokConnected}
      tiktokFollowerCount={tiktokFollowerCount}
      tiktokProfileLink={tiktokProfileLink}
      isTikTokSocialLinkingEnabled={isTikTokSocialLinkingEnabled}
    />
  );
};

export default MySocials;
